import { Box, Button, ButtonDropdown, Header, SpaceBetween } from "@amzn/awsui-components-react";
import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import { MapSide, SideConfig, usePanelStore, ViewerPanelId } from "../../state";
import SideConfigForm from "./SideConfigForm";
import { SideOptions } from "./ConfigBar";

export interface OptionsMenuPanelProps {
    draft: { left: SideConfig; right: SideConfig };
    options: SideOptions;
    dirty: boolean;
    refreshing?: boolean;
    updating?: boolean;
    onChange: (side: MapSide, patch: Partial<SideConfig>) => void;
    onRefresh: () => void;
    onUpdate: () => void;
}

const WRAPPER: CSSProperties = { position: "absolute", top: 0, left: 0, zIndex: 4, width: "fit-content" };
const PANEL: CSSProperties = {
    marginTop: 4,
    marginLeft: 8,
    width: 360,
    maxWidth: "calc(100vw - 40px)",
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    padding: 16,
};

/** Options dropdown items mirroring the 1.0 MapActions menu structure. */
const MENU_ITEMS = [
    {
        text: "Map",
        items: [
            { text: "Map Configuration", id: "map-configuration" },
            { text: "Layers", id: "layers" },
            { text: "Stylesheet - Sprites", id: "sprites" },
        ],
    },
    {
        text: "Navigate To",
        items: [
            { text: "Zoom to Tile", id: "zoom-to-tile" },
            { text: "Zoom to XY", id: "zoom-to-xy" },
        ],
    },
    {
        text: "Map Actions",
        items: [
            { text: "GeoJSON", id: "geojson" },
            { text: "BBox", id: "bbox" },
        ],
    },
];

/**
 * The on-map "Options" control (chosen single-view layout). A compact dropdown
 * floats flush in the map's top-left corner — mirroring the 1.0 MapActions menu
 * (Map Configuration, Layers, Sprites, Zoom-to-Tile/XY, GeoJSON, BBox). "Map
 * Configuration" toggles the inline config panel here; the rest open the v2
 * side-panel host. The map fills the full viewport beneath.
 */
const OptionsMenuPanel: FC<OptionsMenuPanelProps> = ({
    draft,
    options,
    dirty,
    refreshing = false,
    updating = false,
    onChange,
    onRefresh,
    onUpdate,
}) => {
    const [configOpen, setConfigOpen] = useState(false);
    const togglePanel = usePanelStore((s) => s.togglePanel);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const onItemClick = (id: string) => {
        if (id === "map-configuration") {
            setConfigOpen((o) => !o);
        } else {
            togglePanel(id as ViewerPanelId);
        }
    };

    // Close the config panel on an outside click (feedback #4).
    useEffect(() => {
        if (!configOpen) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (wrapperRef.current && wrapperRef.current.contains(target)) {
                return;
            }
            // The Select dropdowns use `expandToViewport`, so their option lists
            // render in a Cloudscape portal OUTSIDE this panel's DOM. Treat clicks
            // inside any awsui portal as "inside" so picking an option (e.g. a
            // stylesheet) doesn't close the config panel.
            const el = target as HTMLElement;
            if (el.closest && el.closest('[class*="awsui_dropdown"], [class*="awsui_options-list"], .awsui-portal')) {
                return;
            }
            setConfigOpen(false);
        };
        // Defer so the opening click itself doesn't immediately close it.
        const id = window.setTimeout(() => document.addEventListener("mousedown", onDocClick), 0);
        return () => {
            window.clearTimeout(id);
            document.removeEventListener("mousedown", onDocClick);
        };
    }, [configOpen]);

    return (
        <div style={WRAPPER} ref={wrapperRef}>
            <ButtonDropdown
                variant="primary"
                items={MENU_ITEMS as any}
                onItemClick={({ detail }) => onItemClick(detail.id)}
            >
                Options
            </ButtonDropdown>

            {configOpen && (
                <div style={PANEL}>
                    <SpaceBetween size="m">
                        <Header
                            variant="h3"
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    {/* Native title tooltips (hover) clarify the Refresh-vs-Update
                                        distinction. Cloudscape Popover is click-triggered in this
                                        version, so a title attr is the right hover affordance. */}
                                    <span title="Re-fetches the stylesheet and version lists. Does not change the map.">
                                        <Button iconName="refresh" loading={refreshing} onClick={onRefresh}>
                                            Refresh
                                        </Button>
                                    </span>
                                    <span title="Applies the selected configuration and reloads the map.">
                                        <Button variant="primary" loading={updating} onClick={onUpdate}>
                                            {dirty ? "Update •" : "Update"}
                                        </Button>
                                    </span>
                                    <Button
                                        iconName="close"
                                        variant="icon"
                                        ariaLabel="Close map configuration"
                                        onClick={() => setConfigOpen(false)}
                                    />
                                </SpaceBetween>
                            }
                        >
                            Map configuration
                        </Header>
                        <SideConfigForm
                            config={draft.left}
                            sourceOptions={options.sourceOptions}
                            stylesheetOptions={options.stylesheetOptions}
                            versionOptions={options.versionOptions}
                            onChange={(patch) => onChange("left", patch)}
                        />
                        <Box variant="small" color="text-status-inactive">
                            Edit fields, then Update to apply to the map.
                        </Box>
                    </SpaceBetween>
                </div>
            )}
        </div>
    );
};

export default OptionsMenuPanel;
